import React from "react";
import {
  Banner,
  Card,
  Flex,
  Link,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Text,
} from "@jsq/ds/core";
import { Icon } from "@jsq/ds/icons";
import { Colors } from "@jsq/ds/styles";
import Money from "../../../../Money";
import { PositionRowData } from "../../../types";
import { getPaymentMethodDisplay } from "../../SelectPositionsStep/SelectPositionsTable/columnDefinitions";
import {
  INVALID_PAYMENT_INSTRUCTIONS_TOOLTIP_TEXT,
  NO_PAYMENT_INSTRUCTIONS_BANNER_TEXT,
  PAYMENT_PREF_INVALID_INSTRUCTIONS_GENERAL_TOOLTIP_TEXT,
  UNSUPPORTED_METHOD_BANNER_TEXT,
  UnableToPayReason,
} from "../../SelectPositionsStep/SelectPositionsTable/constants";
import { PayingCardStyle } from "../styles_vanilla.css";
import { tableCell, tableRow } from "./styles_vanilla.css";

export type PayingCardProps = {
  position: PositionRowData;
};

const getBannerDescription = (position: PositionRowData): string => {
  if (position.invalidPaymentInstructionReason) {
    return INVALID_PAYMENT_INSTRUCTIONS_TOOLTIP_TEXT[position.invalidPaymentInstructionReason];
  }
  if (position.unselectableReason === UnableToPayReason.NoPaymentInstructions) {
    return NO_PAYMENT_INSTRUCTIONS_BANNER_TEXT;
  }
  if (position.unselectableReason === UnableToPayReason.UnsupportedMethod) {
    const method = getPaymentMethodDisplay(position.paymentMethod);
    return UNSUPPORTED_METHOD_BANNER_TEXT.replace("{{method}}", method ? `(${method}) ` : "");
  }
  return position.unselectableReason ?? PAYMENT_PREF_INVALID_INSTRUCTIONS_GENERAL_TOOLTIP_TEXT;
};

export const PayingCard: React.FC<PayingCardProps> = ({ position }) => {
  const hasIssue = !position.isSelectable;

  const paymentInstructions = position.bankAccountInstitutionName
    ? position.bankAccountLastFour
      ? `${position.bankAccountInstitutionName} ····${position.bankAccountLastFour}`
      : position.bankAccountInstitutionName
    : null;

  return (
    <div className={PayingCardStyle}>
      {/* Warning banner */}
      {hasIssue && (
        <div style={{ marginBottom: "16px" }}>
          <Banner
            variant="warning"
            title="Unable to pay this account"
            description={getBannerDescription(position)}
          />
        </div>
      )}

      {/* PAYING: sub-section — nested card */}
      <Card padding="lg">
        <Flex flexDirection="column" gap={12}>
          <Text variant="secondary-md">PAYING:</Text>
          <Table condensed>
            <TableBody>
              <TableRow className={tableRow}>
                <TableCell className={tableCell}>
                  <Text>Account</Text>
                </TableCell>
                <TableCell>
                  {position.positionUrl ? (
                    <Link
                      href={position.positionUrl}
                      icon="external-link"
                      iconPosition="right"
                      shouldOpenInNewTab
                    >
                      {position.positionName}
                    </Link>
                  ) : (
                    <Text>{position.positionName}</Text>
                  )}
                </TableCell>
              </TableRow>
              {paymentInstructions && (
                <TableRow className={tableRow}>
                  <TableCell className={tableCell}>
                    <Text>Payment instructions</Text>
                  </TableCell>
                  <TableCell>
                    <Text>{paymentInstructions}</Text>
                  </TableCell>
                </TableRow>
              )}
              {position.paymentMethod && (
                <TableRow className={tableRow}>
                  <TableCell className={tableCell}>
                    <Text>Payment method</Text>
                  </TableCell>
                  <TableCell>
                    <Text>{getPaymentMethodDisplay(position.paymentMethod)}</Text>
                  </TableCell>
                </TableRow>
              )}
              {hasIssue && (
                <TableRow className={tableRow}>
                  <TableCell className={tableCell}>
                    <Text>Payment method status</Text>
                  </TableCell>
                  <TableCell>
                    <Flex alignItems="center" gap={4}>
                      <Icon color={Colors.Danger600} type="exclamation-triangle" size="small" />
                      <Text color={Colors.Danger600}>{position.unselectableReason}</Text>
                    </Flex>
                  </TableCell>
                </TableRow>
              )}
              <TableRow className={tableRow}>
                <TableCell className={tableCell}>
                  <Text>Net payment amount</Text>
                </TableCell>
                <TableCell>
                  <Money money={position.netPayment} rounded={false} />
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Flex>
      </Card>
    </div>
  );
};
